import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { agentAttachmentService } from '../../services/agentAttachmentService';
import { AttachAgentDialog } from './AttachAgentDialog';
import type { AgentAttachmentDto } from '../../types/agentAttachment';

interface AttachedAgentsPanelProps {
  parentAgentId: string;
}

export const AttachedAgentsPanel = ({ parentAgentId }: AttachedAgentsPanelProps) => {
  const [attachments, setAttachments] = useState<AgentAttachmentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await agentAttachmentService.getAttachments(parentAgentId);
      setAttachments(data);
    } finally {
      setLoading(false);
    }
  }, [parentAgentId]);

  useEffect(() => { load(); }, [load]);

  const handleAttach = async (childId: string, alias?: string, descriptionOverride?: string) => {
    await agentAttachmentService.attach(parentAgentId, {
      childAgentDefinitionId: childId,
      alias,
      descriptionOverride,
      sortOrder: attachments.length,
    });
    load();
  };

  const handleDetach = async (attachmentId: string) => {
    await agentAttachmentService.detach(parentAgentId, attachmentId);
    load();
  };

  const excludeIds = [parentAgentId, ...attachments.map((a) => a.childAgentDefinitionId)];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Attached Sub-Agents
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Attach
        </Button>
      </Box>

      {loading && <CircularProgress size={20} />}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {attachments.map((att) => (
          <Chip
            key={att.id}
            label={att.alias || att.childAgentName}
            size="small"
            color={att.isEnabled ? 'primary' : 'default'}
            variant="outlined"
            onDelete={() => handleDetach(att.id)}
            deleteIcon={
              <Tooltip title="Detach">
                <DeleteIcon fontSize="small" />
              </Tooltip>
            }
          />
        ))}
        {attachments.length === 0 && !loading && (
          <Typography variant="caption" color="text.secondary">
            No sub-agents attached. Click "Attach" to add agents that the orchestrator can call.
          </Typography>
        )}
      </Box>

      <AttachAgentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAttach={handleAttach}
        excludeIds={excludeIds}
      />
    </Box>
  );
};
